// Everything we need to read out of an auth redirect URL.
//
// Two reasons this lives outside the screens that use it: Supabase strips the
// auth fragment from window.location while the client initialises, and it fires
// PASSWORD_RECOVERY before any component has mounted. Both signals are captured
// here at module load, so a screen can still ask about them later.
import { supabase } from './supabase';

function paramsOf(href: string): URLSearchParams | null {
  try {
    const url = new URL(href);
    const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    const merged = new URLSearchParams(hash);
    url.searchParams.forEach((v, k) => {
      if (!merged.has(k)) merged.append(k, v);
    });
    return merged;
  } catch {
    return null;
  }
}

/** A password-reset link brings the user back with `type=recovery`. */
export function isRecoveryLink(href: string) {
  return paramsOf(href)?.get('type') === 'recovery';
}

export type AuthRedirectError = { code: string | null; description: string | null };

/**
 * Supabase reports a refused or stale link as `error`/`error_code`/
 * `error_description` on the redirect instead of returning a session.
 */
export function parseAuthRedirectError(href: string): AuthRedirectError | null {
  const p = paramsOf(href);
  if (!p) return null;
  const error = p.get('error') || p.get('error_code');
  if (!error) return null;
  return {
    code: p.get('error_code') || p.get('error'),
    description: p.get('error_description')?.replace(/\+/g, ' ') || null,
  };
}

/** Friendly, translated text for the failures users actually hit. */
export function describeAuthRedirectError(
  err: AuthRedirectError,
  t: (key: string, fallback?: string) => string
) {
  const code = (err.code || '').toLowerCase();
  if (code.includes('expired') || code === 'otp_expired') {
    return t(
      'This link has expired or was already used. Request a new one and open it right away.',
      'This link has expired or was already used. Request a new one and open it right away.'
    );
  }
  if (code === 'access_denied') {
    return t('Sign-in was cancelled.', 'Sign-in was cancelled.');
  }
  // Anything else: Supabase's own wording beats a vague catch-all.
  return err.description || t('Sign-in failed', 'Sign-in failed');
}

let recoveryPending = typeof window !== 'undefined' && isRecoveryLink(window.location.href);

supabase.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY') recoveryPending = true;
});

/** Native deep links never touch window.location, so they report in by hand. */
export function noteRecoveryLink(href: string) {
  if (isRecoveryLink(href)) recoveryPending = true;
}

export const isPasswordRecoveryPending = () => recoveryPending;
export const clearPasswordRecovery = () => {
  recoveryPending = false;
};

export const RESET_PASSWORD_PATH = '/auth/reset-password';
